import { useEffect, useCallback, useMemo } from 'react';
import { useNavigationType, useLocation } from 'react-router-dom';
import useQuestionBankStore from '../store/questionBankStore';
import useCreateTestPathwayStore from '../store/createTestPathwayStore';
import debounce from '../utils/debounce';
import withRetry from '../utils/withRetry';

const useQuestionBankPageState = () => {
  const {
    questions,
    loading,
    error,
    searchQuestions,
    availableMonths,
    availableYears,
    monthYearsLoading,
    fetchMonthYears,
    resetStore,
    currentPage: page,
    setCurrentPage: setPage,
    rowsPerPage, 
    searchTerm,
    setSearchTerm,
    searchId,
    setSearchId,
    month,
    setMonth,
    year,
    setYear
  } = useQuestionBankStore();

  const {
    courses, syllabuses, subjects, units, modules, topics,
    selectedCourse, selectedSyllabus, selectedSubject, selectedUnit, selectedModule, selectedTopic,
    fetchInitialData, selectCourse, selectSyllabus, selectSubject, selectUnit, selectModule, selectTopic,
  } = useCreateTestPathwayStore();

  const runSearch = useCallback((overrides = {}) => {
    const payload = {
      keyword: searchTerm,
      courseCode: selectedCourse,
      sylCode: selectedSyllabus,
      subCode: selectedSubject,
      unitCode: selectedUnit,
      moduleCode: selectedModule,
      topicCode: selectedTopic,
      month,
      year,
      ...overrides,
    };
    withRetry(() => searchQuestions(payload));
  }, [searchTerm, selectedCourse, selectedSyllabus, selectedSubject, selectedUnit, selectedModule, selectedTopic, month, year, searchQuestions]);

  const updateMonthYears = useCallback((overrides = {}) => {
    const filters = {
      courseCode: selectedCourse,
      sylCode: selectedSyllabus,
      subCode: selectedSubject,
      unitCode: selectedUnit,
      moduleCode: selectedModule,
      topicCode: selectedTopic,
      month,
      year,
      ...overrides,
    };
    fetchMonthYears(filters);
  }, [selectedCourse, selectedSyllabus, selectedSubject, selectedUnit, selectedModule, selectedTopic, month, year, fetchMonthYears]);

  const debouncedSearch = useCallback(debounce(runSearch, 250), [runSearch]);

  const navType = useNavigationType();
  const location = useLocation();

  useEffect(() => {
    fetchInitialData();

    if (navType !== 'POP') {
      try {
        sessionStorage.removeItem('question-bank-storage');
      } catch (e) {}
      setSearchTerm('');
      setSearchId('');
      setMonth('');
      setYear('');
      setPage(1);
      selectCourse('');
      runSearch({
        keyword: '',
        courseCode: '',
        sylCode: '',
        subCode: '',
        unitCode: '',
        moduleCode: '',
        topicCode: '',
        month: '',
        year: '',
      });
      updateMonthYears({
        courseCode: '',
        sylCode: '',
        subCode: '',
        unitCode: '',
        moduleCode: '',
        topicCode: '',
        month: '',
        year: '',
      });
    } else {
      runSearch({});
      updateMonthYears({});
    }
  }, [location.key]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPage(1);
    debouncedSearch({ keyword: term });
  };

  const handleSearchId = (id) => {
    setSearchId(id);
    setPage(1);
  };

  const handleMonthChange = (value) => {
    setMonth(value);
    setPage(1);
    const overrides = { month: value };
    debouncedSearch(overrides);
    updateMonthYears(overrides);
  };

  const handleYearChange = (value) => {
    setYear(value);
    setPage(1);
    const overrides = { year: value };
    debouncedSearch(overrides);
    updateMonthYears(overrides);
  };

  const handleCourseChange = (value) => {
    selectCourse(value);
    setPage(1);
    const overrides = { courseCode: value, sylCode: '', subCode: '', unitCode: '', moduleCode: '', topicCode: '' };
    debouncedSearch(overrides);
    updateMonthYears(overrides);
  };

  const handleSyllabusChange = (value) => {
    selectSyllabus(value);
    setPage(1);
    const overrides = { sylCode: value, subCode: '', unitCode: '', moduleCode: '', topicCode: '' };
    debouncedSearch(overrides);
    updateMonthYears(overrides);
  };

  const handleSubjectChange = (value) => {
    selectSubject(value);
    setPage(1);
    const overrides = { subCode: value, unitCode: '', moduleCode: '', topicCode: '' };
    debouncedSearch(overrides);
    updateMonthYears(overrides);
  };

  const handleUnitChange = (value) => {
    selectUnit(value);
    setPage(1);
    const overrides = { unitCode: value, moduleCode: '', topicCode: '' };
    debouncedSearch(overrides);
    updateMonthYears(overrides);
  };

  const handleModuleChange = (value) => {
    selectModule(value);
    setPage(1);
    const overrides = { moduleCode: value, topicCode: '' };
    debouncedSearch(overrides);
    updateMonthYears(overrides);
  };

  const handleTopicChange = (value) => {
    selectTopic(value);
    setPage(1);
    const overrides = { topicCode: value };
    debouncedSearch(overrides);
    updateMonthYears(overrides);
  };

  const safeQuestions = useMemo(() => {
    let list = Array.isArray(questions) ? [...questions] : [];
    if (searchId && String(searchId).trim() !== '') {
      const cleanSearch = String(searchId).replace(/^id[:\s#]*/i, '').trim().toLowerCase();
      if (cleanSearch) {
        list = list.filter((q) => {
          const idStr = q.id !== undefined && q.id !== null ? String(q.id).toLowerCase() : '';
          const uuidStr = q.uuid !== undefined && q.uuid !== null ? String(q.uuid).toLowerCase() : '';
          return idStr === cleanSearch || uuidStr === cleanSearch || idStr.includes(cleanSearch) || uuidStr.includes(cleanSearch);
        });
      }
    }
    return list.sort((a, b) => {
      if (searchId && String(searchId).trim() !== '') {
        const cleanSearch = String(searchId).replace(/^id[:\s#]*/i, '').trim().toLowerCase();
        const idAStr = String(a.id ?? a.uuid ?? '').toLowerCase();
        const idBStr = String(b.id ?? b.uuid ?? '').toLowerCase();
        const aExact = idAStr === cleanSearch;
        const bExact = idBStr === cleanSearch;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;
      }
      const idA = a.id || a.uuid || 0;
      const idB = b.id || b.uuid || 0;
      return idA < idB ? -1 : idA > idB ? 1 : 0;
    });
  }, [questions, searchId]);
  
  const paginatedQuestions = useMemo(() => {
    return safeQuestions.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  }, [safeQuestions, page, rowsPerPage]);

  const reset = useCallback(() => {
    setSearchTerm('');
    setSearchId('');
    setPage(1);
    setMonth('');
    setYear('');
    selectCourse(''); 
    resetStore();
  }, [selectCourse, resetStore, setSearchTerm, setSearchId, setPage, setMonth, setYear]);

  return {
    questions,
    loading,
    error,
    courses,
    syllabuses,
    subjects,
    units,
    modules,
    topics,
    selectedCourse,
    selectedSyllabus,
    selectedSubject,
    selectedUnit,
    selectedModule,
    selectedTopic,
    searchTerm,
    searchId,
    page,
    rowsPerPage,
    month,
    year,
    availableMonths,
    availableYears,
    monthYearsLoading,
    setPage,
    safeQuestions,
    paginatedQuestions,
    handleSearch,
    handleSearchId,
    handleMonthChange,
    handleYearChange,
    handleCourseChange,
    handleSyllabusChange,
    handleSubjectChange,
    handleUnitChange,
    handleModuleChange,
    handleTopicChange,
    reset
  };
};

export default useQuestionBankPageState;
